import React, { useState } from "react";
import { IconButton } from "@material-ui/core";
import LightTooltip from "./LightTooltip";
import { EditOutlined } from "@material-ui/icons";
import { Pane, Text, CDialog, HFlex } from "shared";
import { useTranslation } from "react-i18next";
import { SPACING, Breakpoint } from "theme";
import { css } from "@emotion/css";
import { useStates } from "shared/ValueTable";
import { HealthCareOrganization, useCountries } from "../queries/useHealthCareOrganizations";
import { HealthCareOrganizationForm } from "./HealthCareOrganizationForm";

interface Props {
  hco: HealthCareOrganization;
}

const HcoInfo: React.VFC<Props> = ({ hco }) => {
  const { t } = useTranslation();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const { data: states } = useStates();
  const { data: countries } = useCountries();

  const openDialog = () => setDialogOpen(true);
  const closeDialog = () => setDialogOpen(false);

  const countryName = hco.country_id != null
    ? countries?.find(c => c.id === hco.country_id)?.name ?? hco.country_id
    : null;

  const stateName = hco.us_state_id != null
    ? states?.find(s => s.id === hco.us_state_id)?.value ?? hco.us_state_id
    : null;

  const orgSizeRangeLabels: Record<string, string> = {
    size_1_10: "1–10",
    size_11_25: "11–25",
    size_26_50: "26–50",
    size_51_100: "51–100",
    size_101_250: "101–250",
    size_251_500: "251–500",
    size_501_1000: "501–1000",
    size_1001_plus: "1001+",
  };

  const details = [
    [t("form.hco_status"), hco.status],
    [t("form.hco_contact_email"), hco.contact_email],
    [t("form.hco_billing_email"), hco.billing_email],
    [t("settings.profile_information.time_zone"), hco.iana_timezone],
    [t("form.hco_organization_size_range"), hco.organization_size_range ? orgSizeRangeLabels[hco.organization_size_range] ?? hco.organization_size_range : null],
    [t("form.hco_address"), hco.address],
    [t("form.hco_city"), hco.city],
    [t("form.hco_zip_code"), hco.zip_code],
    [t("form.hco_country"), countryName],
    [t("form.hco_state"), stateName],
    [t("form.created_at"), hco.created_at],
  ].filter(([, value]) => value != null && value !== "");

  return (
    <>
      <Pane dark mb={2}>
        <HFlex justifyContent="space-between" alignItems="flex-start">
          <div className={styles.grid}>
            {details.map(([label, value]) => (
              <React.Fragment key={String(label)}>
                <Text light nowrap>
                  {label}
                </Text>
                <Text bold nowrap>
                  {value}
                </Text>
              </React.Fragment>
            ))}
          </div>
          <LightTooltip title={t("buttons.edit")}>
            <IconButton onClick={openDialog} size="small" aria-label={t("buttons.edit")}>
              <EditOutlined fontSize="small" style={{ color: "#1976d2" }} />
            </IconButton>
          </LightTooltip>
        </HFlex>
      </Pane>

      <CDialog maxWidth="sm" fullWidth open={isDialogOpen} onClose={closeDialog}>
        <HealthCareOrganizationForm organization={hco} onSuccess={closeDialog} onCancel={closeDialog} />
      </CDialog>
    </>
  );
};

export default HcoInfo;

const styles = {
  grid: css`
    overflow: auto;
    display: grid;
    grid-auto-rows: 45px;
    align-items: center;
    grid-column-gap: ${SPACING}px;
    @media screen and (max-width: ${Breakpoint.sm - 1}px) {
      grid-template-columns: 1fr;
    }
    @media screen and (min-width: ${Breakpoint.sm}px) {
      grid-template-columns: auto 1fr;
    }
  `,
};